import { useState } from 'react'
import { Link } from 'react-router-dom'
import { OriginLink } from '@/lib/readiness/nav'
import type { StepState } from '@/lib/catalogue/engine'
import { capsOf, monthLabel, rollup } from '@/lib/catalogue/engine'
import type { CatalogueCapability, CatalogueTechnology, CatalogueUsage } from '@/api/types/catalogue'
import { CAPABILITY_LABEL } from '@/api/types/catalogue'
import { PqcChip, TechMark } from '@/components/catalogue/shared'
import { confidenceText } from '@/lib/catalogue/summary'
import type { StepChoice } from '@/lib/readiness/scope'
import { fmtMonth } from '@/lib/readiness/projection'
import { Chip, Icon, MoreToggle, SectionLabel } from '@/components/readiness/primitives'
import type { MigrationFacts } from '@/api/types/migration'
import { formatDate } from '@/components/common/format'

// ── Dependency-aware objective detail (Iteration 3 §12) ─────────────────────

// Per-step dependency line: ready (green) or depends on (amber).
export function StepDependency({ st, facts, q }: { st: StepState; facts: MigrationFacts | null; q: string }) {
  if (st.state === 'none') return null
  const deps = st.state === 'vendor_gated' ? st.gated : st.dependencies
  return (
    <div className="mt-2 space-y-1" data-testid={`step-dep-${st.bullet_index}`}>
      {deps.map((d) => {
        const sys = facts?.systems.find((s) => s.id === d.asset?.system_id)?.name
        const comp = facts?.components.find((c) => c.id === d.asset?.component_id)?.name
        return (
          <p key={`${d.technology.id}-${d.item.id}`} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12.5px]" style={{ color: 'var(--rd-text-secondary)' }}>
            {d.gated ? <Chip tone="warn" style={{ fontWeight: 600 }}>Depends on</Chip> : <Chip tone="ok" style={{ fontWeight: 600 }}>Dependencies ready</Chip>}
            <span className="font-medium">{d.technology.name}</span>
            <span>· {CAPABILITY_LABEL[d.capability]}</span>
            <PqcChip status={d.status} date={d.roadmap_date} />
            {d.gated && d.roadmap_date && <span style={{ color: 'var(--rd-status-warn)' }}>roadmap {monthLabel(d.roadmap_date)} · {confidenceText(d.record?.roadmap_confidence)}</span>}
            <OriginLink to={`/readiness/catalogue/${d.technology.id}${q}`} className="rd-link">Catalogue ↗</OriginLink>
            {d.gated && d.asset && <span className="w-full text-[11.5px]" style={{ color: 'var(--rd-text-faint)' }}><span className="rd-mono">{d.asset.code}</span> · {sys} › {comp ?? '—'} · via <Link to={`/readiness/tracker${q}&item=${d.item.id}`} className="rd-link">{d.item.code}</Link>{facts?.systems.find((s) => s.id === d.asset?.system_id)?.last_scan_at ? ` · last seen ${formatDate(facts.systems.find((s) => s.id === d.asset?.system_id)!.last_scan_at)}` : ''}</span>}
          </p>
        )
      })}
    </div>
  )
}

// Options on a gated step: wait (default) · pilot · waiver.
export function GatedOptions({ st, choice, alternatives, category, onChoose, onCompare, onWaiver, onPlaybook }: { st: StepState; choice: StepChoice | null; alternatives: { ready: number; total: number }; category: string; onChoose: (o: 'wait' | 'pilot') => void; onCompare: () => void; onWaiver: () => void; onPlaybook: () => void }) {
  const option = choice?.option ?? 'wait'
  const done = st.vendor_date ? new Date(new Date(`${st.vendor_date}-01T00:00:00Z`).getTime() + 30 * 86_400_000).toISOString() : null
  const tile = (key: 'wait' | 'pilot' | 'waiver', title: string, body: React.ReactNode, chip?: string) => (
    <div className="h-full rounded-[10px] p-3 text-[12.5px]" style={{ border: `1px solid ${option === key ? 'var(--rd-accent-primary)' : 'var(--rd-border-subtle)'}`, background: option === key ? 'color-mix(in srgb, var(--rd-accent-primary) 8%, transparent)' : 'var(--rd-bg-card)' }} data-testid={`option-${key}`}>
      <p className="flex items-center gap-2 text-[13px] font-bold">{title}{chip && option === key && <Chip tone="info">{chip}</Chip>}</p>
      <div className="mt-1" style={{ color: 'var(--rd-text-muted)' }}>{body}</div>
    </div>
  )
  return (
    <div className="mt-3" data-testid={`gated-options-${st.bullet_index}`}>
      <div className="grid items-stretch gap-2 md:grid-cols-3">
        {tile('wait', 'Wait for the vendor', <>The item and step follow the catalogue date; the owner is notified if it moves.{done && <span className="mt-1 block text-[12px]" style={{ color: 'var(--rd-text-secondary)' }}>Step done ≈ {fmtMonth(done)}</span>}</>, 'tracking')}
        {tile('pilot', 'Pilot on a PQC-ready alternative', <>{alternatives.ready} of {alternatives.total} {category.toLowerCase()} in the catalogue are PQC-ready · <i>a pilot, not a replacement decision</i></>, 'piloting')}
        {tile('waiver', 'Record a waiver for the gap', <>Records the objective, step, item, technology and the vendor date as expiry against this step, which then reads <b>deferred, justified</b>.</>, 'deferred, justified')}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <button type="button" className="rd-btn rd-btn-primary rd-btn-sm" onClick={() => onChoose('wait')} data-testid="keep-tracking">Keep tracking vendor date</button>
        <button type="button" className="rd-btn rd-btn-sm" onClick={onCompare} data-testid="compare-alternatives">Compare alternatives</button>
        <button type="button" className="rd-btn rd-btn-sm" onClick={onWaiver} data-testid="record-waiver">Record waiver</button>
        <button type="button" className="rd-btn rd-btn-sm" onClick={onPlaybook}>Create Playbook action</button>
      </div>
    </div>
  )
}

export function ExternalDependenciesCard({ code, assetIds, usage, technologies, capabilities, q }: { code: string; assetIds: string[]; usage: CatalogueUsage[]; technologies: CatalogueTechnology[]; capabilities: CatalogueCapability[]; q: string }) {
  const [all, setAll] = useState(false)
  const inScope = new Set(assetIds)
  const byTech = new Map<string, number>()
  usage.filter((u) => inScope.has(u.asset_id)).forEach((u) => byTech.set(u.technology_id, (byTech.get(u.technology_id) ?? 0) + 1))
  // Gated (not PQC today) first, then by name; five shown, the rest behind +n more.
  const rows = Array.from(byTech.entries())
    .map(([id, n]) => ({ t: technologies.find((x) => x.id === id)!, n }))
    .filter((r) => r.t)
    .map((r) => ({ ...r, status: rollup(capsOf(r.t.id, capabilities)) }))
    .sort((a, b) => Number(GATED.includes(b.status)) - Number(GATED.includes(a.status)) || a.t.name.localeCompare(b.t.name))
  const shown = all ? rows : rows.slice(0, CAP)
  return (
    <section className="rd-card p-5" aria-labelledby="rd-ext" data-testid="external-dependencies">
      <SectionLabel><span id="rd-ext">External dependencies · {code}</span></SectionLabel>
      <ul className="mt-2 space-y-2 text-[12.5px]">
        {shown.map(({ t, n, status }) => {
          const caps = capsOf(t.id, capabilities)
          return (
            <li key={t.id} className="flex items-center gap-2" data-testid={`ext-dep-${t.id}`}>
              <TechMark tech={t} size={24} />
              <span className="min-w-0 flex-1"><OriginLink to={`/readiness/catalogue/${t.id}${q}`} className="block truncate hover:underline">{t.name}</OriginLink><span style={{ color: 'var(--rd-text-faint)' }}>{caps.map((c) => CAPABILITY_LABEL[c.capability]).slice(0, 2).join(', ')} · {n} {n === 1 ? 'asset' : 'assets'}</span></span>
              <PqcChip status={status} />
            </li>
          )
        })}
        {rows.length === 0 && <li style={{ color: 'var(--rd-text-muted)' }}>No external technology detected in this objective's scope.</li>}
      </ul>
      <MoreToggle total={rows.length} cap={CAP} open={all} onToggle={() => setAll((v) => !v)} testId="ext-deps-more" />
      <Link to={`/readiness/catalogue${q}`} className="rd-link mt-3 flex text-[12.5px]">All dependencies in the catalogue →</Link>
    </section>
  )
}

const CAP = 5
const GATED = ['roadmap', 'none']

// Earliest achievable (I3 §12.2, fixed in I5 §14b): the vendor row renders only
// for a gated step and never falls back to the pace line; without a gate a
// single `L3 · at current pace` line (plus the next level when it isn't L3).
export function EarliestAchievableCard({ next, gatedStep, waiting, alternative, l3 }: { next: string | null; gatedStep: number | null; waiting: string | null; alternative: string | null; l3: string | null }) {
  const gated = next != null && gatedStep != null
  return (
    <section className="rd-card p-5" aria-labelledby="rd-earliest" data-testid="earliest-achievable">
      <SectionLabel><span id="rd-earliest">Earliest achievable</span></SectionLabel>
      <dl className="mt-2 space-y-1.5 text-[14px]">
        {gated && <div className="flex justify-between gap-2"><dt style={{ color: 'var(--rd-text-muted)' }}>{next} · waiting for vendor</dt><dd className="rd-mono rd-id" style={{ color: 'var(--rd-status-warn)' }}>{fmtMonth(waiting)}</dd></div>}
        {gated && <div className="flex justify-between gap-2"><dt style={{ color: 'var(--rd-text-muted)' }}>{next} · alternative or waiver for step {gatedStep}</dt><dd className="rd-mono rd-id">{fmtMonth(alternative)}</dd></div>}
        {!gated && next && next !== 'L3' && <div className="flex justify-between gap-2"><dt style={{ color: 'var(--rd-text-muted)' }}>{next} · at current pace</dt><dd className="rd-mono rd-id">{fmtMonth(alternative)}</dd></div>}
        <div className="flex justify-between gap-2" data-testid="earliest-l3"><dt style={{ color: 'var(--rd-text-muted)' }}>L3 · at current pace</dt><dd className="rd-mono rd-id" style={{ color: 'var(--rd-projection)' }}>{l3 ? fmtMonth(l3) : 'at L3'}</dd></div>
      </dl>
      <p className="mt-2 text-[11.5px]" style={{ color: 'var(--rd-text-faint)' }}><Icon name="info" size={11} /> Vendor dates come from the catalogue; pace from the event log. Both feed the Progress projection.</p>
    </section>
  )
}
